import { useAuth } from "@/context/auth-provider";
import React from "react";
import { useQuery } from "react-query";
import { TransactionAPI } from "../services/transaction.service";
import { Category, TransactionType } from "../types/transaction.type";
import { DEFAULT_TRANSACTION_TYPE } from "./use-categories";
import { UseFiltersParams } from "./use-filters";

/**
 * Value of the type filter (and the `?type=` URL param) for the
 * Uncategorized tab. Lowercase so it can't collide with a TransactionType name.
 */
export const UNCATEGORIZED_TYPE = "uncategorized";

export const isUncategorizedType = (type?: string) =>
  type === UNCATEGORIZED_TYPE;

// Starts with "transactions" so every invalidateQueries("transactions") after a
// create/edit/delete also refreshes the count.
export const UNCATEGORIZED_COUNT_QUERY_KEY = [
  "transactions",
  "uncategorized-count",
];

interface BuildTransactionFilterParamsArgs {
  filters?: UseFiltersParams;
  search?: string;
  categories?: Category[];
  types?: TransactionType[];
}

/**
 * Filter params shared by the transaction list and export requests.
 *
 * The Uncategorized tab sends `uncategorized=true` and never `type` or
 * `category`: uncategorized rows have no category type, so the backend rejects
 * those combinations.
 */
export const buildTransactionFilterParams = ({
  filters,
  search = "",
  categories,
  types,
}: BuildTransactionFilterParamsArgs) => {
  const uncategorized = isUncategorizedType(filters?.type);
  return {
    category: uncategorized
      ? []
      : (categories
          ?.filter((c) => filters?.categories?.includes(c.name))
          .map((c) => c.id) ?? []),
    ...(uncategorized
      ? { uncategorized: true }
      : { type: types?.find((t) => t.name === filters?.type)?.id || "" }),
    description: search,
    start_date: filters?.startDate,
    end_date: filters?.endDate,
    created: filters?.created,
    custom_categories: filters?.customCategories || false,
  };
};

interface UseUncategorizedTransactionsParams {
  type?: string;
  setType: (type: string) => void;
}

/**
 * Count of the user's uncategorized transactions (category = null), which no
 * type tab shows. Falls back to the default type tab once there are none left
 * while the Uncategorized tab is selected.
 */
const useUncategorizedTransactions = ({
  type,
  setType,
}: UseUncategorizedTransactionsParams) => {
  const { user } = useAuth();
  const isUncategorizedView = isUncategorizedType(type);

  const { data: count, isLoading } = useQuery<number>(
    UNCATEGORIZED_COUNT_QUERY_KEY,
    async () => {
      const response = await TransactionAPI.getAll({
        uncategorized: true,
        limit: 1,
        offset: 0,
      });
      return response?.data?.count ?? 0;
    },
    { enabled: !!user, refetchOnWindowFocus: false },
  );

  React.useEffect(() => {
    if (isUncategorizedView && count === 0) {
      setType(DEFAULT_TRANSACTION_TYPE);
    }
  }, [isUncategorizedView, count, setType]);

  return {
    count: count ?? 0,
    isLoading,
    isUncategorizedView,
    // Keep the tab while a `?type=uncategorized` deep link waits for the count.
    showTab: (count ?? 0) > 0 || (isUncategorizedView && count === undefined),
  };
};

export default useUncategorizedTransactions;
